import React, { useCallback, useEffect, useState } from "react";
import { fetchIngredients, fetchStocktakes, createStocktake, fetchStocktake, getCurrency } from "../services/api";
import { formatPrice } from "../utils/currency";
import { useT } from "../i18n/useT";

// Stocktake — a physical store count reconciled against book (system) stock.
// The gap is store shrinkage (theft / spoilage / errors). Submitting records
// the variance and corrects each ingredient's stock to the counted figure.
const S = {
  en: {
    title: "Stocktake", sub: "Count the store and enter the real quantities. ODRA compares them to the system stock, records the difference (shrinkage), and corrects the stock. Leave a row blank to skip it.",
    ingredient: "Ingredient", system: "System", counted: "Counted", variance: "Variance", value: "Value",
    note: "Note (optional)", save: "Save count", saving: "Saving…", no_ing: "No ingredients yet.",
    history: "Past counts", none_yet: "No stocktakes yet.", net: "Net variance", shrinkage: "Shrinkage",
    saved: "Count saved — stock corrected.", items: "items", date: "Date",
  },
  am: {
    title: "የክምችት ቆጠራ", sub: "መጋዘኑን ቆጥረው ትክክለኛውን መጠን ያስገቡ። ODRA ከሲስተሙ ክምችት ጋር አነጻጽሮ ልዩነቱን (ብክነት) መዝግቦ ክምችቱን ያስተካክላል። ለመዝለል ባዶ ይተዉት።",
    ingredient: "ግብዓት", system: "ሲስተም", counted: "የተቆጠረ", variance: "ልዩነት", value: "ዋጋ",
    note: "ማስታወሻ (አማራጭ)", save: "ቆጠራ አስቀምጥ", saving: "በማስቀመጥ ላይ…", no_ing: "ግብዓት የለም።",
    history: "ያለፉ ቆጠራዎች", none_yet: "እስካሁን ቆጠራ የለም።", net: "ተጣራ ልዩነት", shrinkage: "ብክነት",
    saved: "ቆጠራ ተቀምጧል — ክምችት ተስተካክሏል።", items: "ንጥሎች", date: "ቀን",
  },
  om: {
    title: "Lakkoofsa kuusaa", sub: "Kuusaa lakkaa'ii baay'ina dhugaa galchi. ODRA kuusaa sirna waliin wal bira qaba, garaagarummaa (hir'ina) galmeessee kuusaa sirreessa. Dabarsuuf duwwaa dhiisi.",
    ingredient: "Meeshaa", system: "Sirna", counted: "Lakkaa'ame", variance: "Garaagarummaa", value: "Gatii",
    note: "Yaadannoo (filannoo)", save: "Lakkoofsa olkaa'i", saving: "Olkaa'aa jira…", no_ing: "Meeshaan hin jiru.",
    history: "Lakkoofsawwan darban", none_yet: "Hanga ammaa hin jiru.", net: "Garaagarummaa qulqulluu", shrinkage: "Hir'ina",
    saved: "Lakkoofsi olkaa'ame — kuusaan sirraa'e.", items: "wantoota", date: "Guyyaa",
  },
};

export default function StocktakePage() {
  const { lang } = useT();
  const t = (k) => (S[lang] && S[lang][k]) || S.en[k] || k;
  const cur = getCurrency() || "ETB";
  const money = (v) => formatPrice(v || 0, cur);

  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [note, setNote] = useState("");
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [saved, setSaved] = useState(null);
  const [open, setOpen] = useState(null);

  const load = useCallback(() => {
    fetchIngredients().then(setItems).catch((e) => setErr(e?.response?.data?.detail || "Could not load."));
    fetchStocktakes().then(setHistory).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const varOf = (it) => {
    const c = counts[it.id];
    if (c === "" || c == null) return null;
    return Number(c) - (it.stock_on_hand || 0);
  };

  const submit = async () => {
    const lines = items
      .filter((it) => counts[it.id] !== "" && counts[it.id] != null && !Number.isNaN(Number(counts[it.id])))
      .map((it) => ({ ingredient_id: it.id, counted_qty: Number(counts[it.id]) }));
    if (!lines.length) return;
    setBusy(true); setErr("");
    try {
      const res = await createStocktake({ note: note || null, lines });
      setSaved(res); setCounts({}); setNote("");
      load();
    } catch (e) { setErr(e?.response?.data?.detail || "Could not save."); }
    finally { setBusy(false); }
  };

  const expand = async (id) => {
    if (open && open.id === id) { setOpen(null); return; }
    try { setOpen(await fetchStocktake(id)); } catch { /* ignore */ }
  };

  const inp = { width: 90, textAlign: "right", padding: "6px 8px", borderRadius: 6, border: "1px solid #d6dede" };

  return (
    <>
      <h1>📋 {t("title")}</h1>
      <p className="muted" style={{ marginTop: -4, maxWidth: 720 }}>{t("sub")}</p>

      {err && <div className="error" style={{ marginTop: 8 }}>{err}</div>}
      {saved && (
        <div className="card" style={{ marginTop: 10, padding: "10px 14px", background: "#f2f9f8" }}>
          ✅ {t("saved")} &nbsp; {t("net")}: <b style={{ color: saved.variance_value < 0 ? "#A32D2D" : "#1D9E75" }}>{money(saved.variance_value)}</b>
          {saved.shrinkage_value < 0 && <> &nbsp;·&nbsp; {t("shrinkage")}: <b style={{ color: "#A32D2D" }}>{money(saved.shrinkage_value)}</b></>}
        </div>
      )}

      {/* Count sheet */}
      <div className="card" style={{ marginTop: 12, overflowX: "auto", maxWidth: 820 }}>
        {items.length === 0 ? <p className="muted">{t("no_ing")}</p> : (
          <table className="data" style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr style={{ background: "#008080" }}>
              <th style={{ color: "#fff", textAlign: "left", padding: "8px 10px" }}>{t("ingredient")}</th>
              <th style={{ color: "#fff", textAlign: "right", padding: "8px 10px" }}>{t("system")}</th>
              <th style={{ color: "#fff", textAlign: "right", padding: "8px 10px" }}>{t("counted")}</th>
              <th style={{ color: "#fff", textAlign: "right", padding: "8px 10px" }}>{t("variance")}</th>
              <th style={{ color: "#fff", textAlign: "right", padding: "8px 10px" }}>{t("value")}</th>
            </tr></thead>
            <tbody>
              {items.map((it, i) => {
                const v = varOf(it);
                const val = v == null ? null : v * (it.unit_cost || 0);
                return (
                  <tr key={it.id} style={{ background: i % 2 ? "#f2f9f8" : "#fff" }}>
                    <td style={{ padding: "6px 10px" }}><b>{it.name}</b> <span className="muted" style={{ fontSize: 11 }}>({it.unit})</span></td>
                    <td style={{ padding: "6px 10px", textAlign: "right" }}>{(it.stock_on_hand || 0)}</td>
                    <td style={{ padding: "6px 10px", textAlign: "right" }}>
                      <input type="number" step="any" value={counts[it.id] ?? ""} placeholder="—"
                        onChange={(e) => setCounts((c) => ({ ...c, [it.id]: e.target.value }))} style={inp} />
                    </td>
                    <td style={{ padding: "6px 10px", textAlign: "right", color: v == null ? "#999" : v < 0 ? "#A32D2D" : v > 0 ? "#1D9E75" : "#333" }}>
                      {v == null ? "—" : (v > 0 ? "+" : "") + Math.round(v * 1000) / 1000}
                    </td>
                    <td style={{ padding: "6px 10px", textAlign: "right", color: val == null ? "#999" : val < 0 ? "#A32D2D" : "#333" }}>
                      {val == null ? "—" : money(val)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div style={{ display: "flex", gap: 10, marginTop: 12, alignItems: "center", flexWrap: "wrap", maxWidth: 820 }}>
        <input placeholder={t("note")} value={note} onChange={(e) => setNote(e.target.value)}
          style={{ flex: 1, minWidth: 160, padding: "8px 10px", borderRadius: 8, border: "1px solid #d6dede" }} />
        <button className="btn" disabled={busy} onClick={submit}>{busy ? t("saving") : t("save")}</button>
      </div>

      {/* History */}
      <h3 style={{ marginTop: 22, marginBottom: 8 }}>{t("history")}</h3>
      {history.length === 0 ? <p className="muted">{t("none_yet")}</p> : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6, maxWidth: 820 }}>
          {history.map((st) => (
            <div key={st.id} className="card" style={{ padding: 0 }}>
              <div onClick={() => expand(st.id)} style={{ padding: "10px 14px", display: "flex", justifyContent: "space-between", cursor: "pointer", gap: 10, flexWrap: "wrap" }}>
                <span><b>{st.count_date}</b> <span className="muted" style={{ fontSize: 12 }}>· {st.line_count} {t("items")}</span></span>
                <span>{t("net")}: <b style={{ color: st.variance_value < 0 ? "#A32D2D" : "#1D9E75" }}>{money(st.variance_value)}</b>
                  {st.shrinkage_value < 0 && <span className="muted" style={{ fontSize: 12 }}> · {t("shrinkage")} {money(st.shrinkage_value)}</span>}
                </span>
              </div>
              {open && open.id === st.id && (
                <div style={{ padding: "0 14px 12px", borderTop: "1px solid #eef2f2" }}>
                  <table style={{ width: "100%", fontSize: 13, marginTop: 6 }}>
                    <tbody>
                      {(open.lines || []).map((l) => (
                        <tr key={l.ingredient_id}>
                          <td style={{ padding: "3px 0" }}>{l.name}</td>
                          <td style={{ textAlign: "right" }} className="muted">{l.system_qty} → {l.counted_qty}</td>
                          <td style={{ textAlign: "right", width: 70, color: l.variance < 0 ? "#A32D2D" : l.variance > 0 ? "#1D9E75" : "#333" }}>{l.variance > 0 ? "+" : ""}{l.variance}</td>
                          <td style={{ textAlign: "right", width: 90, color: l.variance_value < 0 ? "#A32D2D" : "#333" }}>{money(l.variance_value)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </>
  );
}
